import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';
import { throwError, of } from 'rxjs';
import { AuthService } from '../services/auth.service';

export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip token logic for session creation endpoint
  if (req.url.includes('/api/session/new')) {
    return next(req);
  }

  const token = authService.getStoredToken();

  // If no token present, fetch one first
  if (!token) {
    console.log('JWT Interceptor - No token found, fetching new token');
    return authService.fetchToken().pipe(
      switchMap(() => {
        const newToken = authService.getStoredToken();
        const modifiedReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${newToken}`,
          },
        });
        return next(modifiedReq);
      }),
      catchError((err) => {
        console.error('JWT Interceptor - Failed to fetch token:', err);
        router.navigate(['/token-expired']);
        return throwError(() => err);
      })
    );
  }

  // Check if existing token is expired
  const isTokenExpired = checkIfTokenExpired(token);

  if (isTokenExpired) {
    console.warn('JWT Interceptor - Token expired, fetching new token');
    return authService.fetchToken().pipe(
      switchMap(() => {
        const newToken = authService.getStoredToken();
        const modifiedReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${newToken}`,
          },
        });
        return next(modifiedReq);
      }),
      catchError((err) => {
        console.error('JWT Interceptor - Failed to refresh token:', err);
        router.navigate(['/token-expired']);
        return throwError(() => err);
      })
    );
  }

  // Token exists and is valid
  const modifiedReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });

  return next(modifiedReq).pipe(
    catchError((err) => {
      if (err.status === 401) {
        console.warn('JWT Interceptor - 401 from backend, fetching new token');
        // Try to refresh token on 401
        return authService.fetchToken().pipe(
          switchMap(() => {
            const newToken = authService.getStoredToken();
            const retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`,
              },
            });
            return next(retryReq);
          }),
          catchError((refreshErr) => {
            console.error('JWT Interceptor - Failed to refresh token after 401:', refreshErr);
            router.navigate(['/token-expired']);
            return throwError(() => err);
          })
        );
      }
      return throwError(() => err);
    })
  );
};

function checkIfTokenExpired(token: string): boolean {
  const now = Date.now();
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiry = payload.exp * 1000; // convert to ms
    return now > expiry;
  } catch (error) {
    console.error('Failed to parse token', error);
    return true; // treat as expired if parsing fails
  }
}
